import APIConfiguration from '@savgroup-front-common/configuration/api';
import {
  COMMON_QUERY_PARAMS,
  SUPPORTED_METHODS,
} from '@savgroup-front-common/constants/shared';
import {
  BackResponse,
  BackResponseFailure,
  INTERVENTION_REPORT_STATE,
  RecipientType,
  ReverseMoneyNumberAmount,
} from '@savgroup-front-common/types';

import { buildUrl, prepareResponseFailure } from '../../helpers';
import { apiCall } from '../../services';

const ENDPOINT = `${APIConfiguration.repairer}files/${COMMON_QUERY_PARAMS.FILE_ID}/interventionreport`;

interface GetInterventionReportArgs {
  fileId: string;
  technicalInterventionId?: string;
}

export enum InterventionReportLineType {
  ADMINISTRATIVE_FEE = 'AdministrativeFee',
  DISCOUNT = 'Discount',
  SERVICE = 'Service',
  UNKNOWN_SERVICE = 'UnknownService',
  SPARE_PART = 'SparePart',
  UNKNOWN_SPARE_PART = 'UnknownSparePart',
}

interface BaseInterventionReportLine {
  id: string;
  description: string;
  preTaxUnitPrice: ReverseMoneyNumberAmount;
  quantity: number;
  vatRate: number;
  preTaxAmount: ReverseMoneyNumberAmount;
  amountIncludedTax: ReverseMoneyNumberAmount;
  recipientType: RecipientType;
  recipientName?: string;
}

interface AdministrativeFeesReportLine extends BaseInterventionReportLine {
  type: InterventionReportLineType.ADMINISTRATIVE_FEE;
}

interface DiscountReportLine extends BaseInterventionReportLine {
  type: InterventionReportLineType.DISCOUNT;
}

interface ServiceReportLine extends BaseInterventionReportLine {
  type: InterventionReportLineType.SERVICE;
  service: {
    serviceId: string;
  };
}

interface UnknownServiceReportLine extends BaseInterventionReportLine {
  type: InterventionReportLineType.UNKNOWN_SERVICE;
  recipientType: RecipientType;
}

interface SparePartReportLine extends BaseInterventionReportLine {
  type: InterventionReportLineType.SPARE_PART;
  sparePart: {
    stockItemId: string;
    sparePartId: string;
    supplierId: string;
    reference: string;
    preTaxUnitBuyingPrice: ReverseMoneyNumberAmount;
    isInstalled?: boolean;
  };
}

interface UnknownSparePartReportLine extends BaseInterventionReportLine {
  type: InterventionReportLineType.UNKNOWN_SPARE_PART;
  unknownSparePart: {
    supplierId: string;
    reference: string;
  };
}

export type GetInterventionReportLine =
  | AdministrativeFeesReportLine
  | DiscountReportLine
  | ServiceReportLine
  | UnknownServiceReportLine
  | SparePartReportLine
  | UnknownSparePartReportLine;

export interface InterventionReportSummary {
  id: string;
  fileId: string;
  model: string;
  closingType: string;
  closingCode: string;
  serialNumber: string;
  irisCode: {
    conditionCode: string;
    symptomCode: string;
    sectionCode: string;
    defectCode: string;
    repairCode: string;
    extendedConditionCode: string;
  };
  technicalDescription: string;
  comment: string;
  state: INTERVENTION_REPORT_STATE;
  reportLines: string[];
  lines: GetInterventionReportLine[];
  validatorUserId: string;
  validatorUserFirstName: string;
  validatorUserLastName: string;
  creationDateUtc: string;
}

export default async function getInterventionReport(
  payload: GetInterventionReportArgs,
): Promise<BackResponse<InterventionReportSummary> | BackResponseFailure> {
  try {
    const url = buildUrl(
      ENDPOINT.replace(COMMON_QUERY_PARAMS.FILE_ID, payload.fileId),
      payload.technicalInterventionId
        ? { technicalInterventionId: payload.technicalInterventionId }
        : undefined,
    );

    return await apiCall<BackResponse<InterventionReportSummary>>(url, {
      method: SUPPORTED_METHODS.GET,
    });
  } catch (exception) {
    return prepareResponseFailure(exception);
  }
}
